import { type BufferGeometry, type Matrix4, Plane, Vector3 } from 'three';
import { meshPlaneIntersectionPoints } from '@/domain/geometry/meshPlaneIntersection';
import { vector3 } from '@/domain/geometry/vector3';
import type { NumberArray3 } from '@/domain/geometry/apiVector';
import type { FittedStem } from '@/views/3d-planner/fitting/fittedStem';
import { logger } from '@/util/logger';

const log = logger();

/** Result of the LT resection distance calculation, including geometry for visualization. */
export type LtResectionResult = {
    /** Signed distance from LT landmark to nearest bone-surface point on the resection plane. */
    distance: number;
    /** The LT-resection landmark position in world space. */
    ltPoint: Vector3;
    /** The nearest intersection point on the resection plane in world space. */
    nearestPoint: Vector3;
};

/** Inputs to {@link calculateLtResectionDistance}, all in world/CT space. */
export type LtResectionInput = {
    /** Operative femur mesh. */
    femurGeometry: BufferGeometry;
    /** Resection plane origin. */
    resectionPlaneOrigin: Vector3;
    /** Resection plane normal. */
    resectionPlaneNormal: Vector3;
    /** LT-resection landmark. */
    ltResectionPoint: NumberArray3;
    /**
     * Femoral shaft axis pointing distally (proximal -> distal). Required — the backend is
     * always signed, so this must never be called without one.
     */
    siAxis: Vector3;
};

/**
 * Calculate the signed distance from the lesser trochanter resection landmark to the nearest
 * bone-surface point on the neck resection plane.
 *
 * This matches the backend algorithm in hip_stem_resection.py:
 *   1. Find intersection points between the femur mesh and the resection plane
 *   2. Find the nearest intersection point to the LT-resection landmark
 *   3. Sign the distance using the femoral shaft axis (SI axis)
 *
 * Sign convention:
 *   - Positive: resection plane is superior to the LT landmark (clinically expected >= 5mm)
 *   - Negative: resection plane is inferior to the LT landmark
 */
export function calculateLtResectionDistance({
    femurGeometry,
    resectionPlaneOrigin,
    resectionPlaneNormal,
    ltResectionPoint,
    siAxis,
}: LtResectionInput): LtResectionResult | null {
    // A degenerate axis means the caller's data is unexpectedly broken — log it rather than hide
    // it silently, so the anomaly surfaces instead of masquerading as an ordinary missing input.
    if (siAxis.lengthSq() < 1e-12) {
        log.warn('LT-resection SI axis is degenerate (zero-length) — hiding the measurement');
        return null;
    }

    const worldPlane = new Plane().setFromNormalAndCoplanarPoint(
        resectionPlaneNormal,
        resectionPlaneOrigin,
    );

    const intersectionPoints = meshPlaneIntersectionPoints(femurGeometry, worldPlane);
    if (intersectionPoints.length === 0) {
        return null;
    }

    const ltWorld = vector3(ltResectionPoint);
    let minDistSq = Infinity;
    let nearestIndex = 0;
    for (let i = 0; i < intersectionPoints.length; i++) {
        const distSq = ltWorld.distanceToSquared(intersectionPoints[i]);
        if (distSq < minDistSq) {
            minDistSq = distSq;
            nearestIndex = i;
        }
    }

    const nearestPoint = intersectionPoints[nearestIndex];
    let distance = ltWorld.distanceTo(nearestPoint);

    // Sign the distance using the SI axis, matching the backend:
    //   si_axis = shaft_axis_direction * -1 (pointing distally)
    //   if dot(si_axis, lt - nearest) < 0: distance *= -1
    const ltToNearest = new Vector3().subVectors(ltWorld, nearestPoint);
    if (ltToNearest.dot(siAxis) < 0) {
        distance *= -1;
    }

    return { distance, ltPoint: ltWorld, nearestPoint };
}

/** Raw inputs needed to resolve the LT-resection result from the planner's current state. */
export type ResolveLtResectionInput = {
    /** The fitted stem (carries the resection plane in stem-local space; present even on legacy
     *  blobs — {@link FittedStem.resectionPlaneLocal} is built unconditionally). */
    fittedStem: FittedStem | null;
    /** The manual stem-group transform (`template.stemTransform`). */
    stemTransform: Matrix4 | null;
    /** Operative femur mesh in world/CT space. */
    femurGeometry: BufferGeometry | null;
    /** LT-resection landmark in world space. */
    ltResectionPoint: NumberArray3 | null;
    /**
     * Femoral shaft-axis direction (world/CT space), from the `hip_femur_shaft_axis` study
     * measurement (`PlannerState.femoralFeatures.shaftAxis.value`) — points proximal (superior);
     * negated internally to get the distal-pointing SI axis, matching the backend's
     * `resection_si_axis`. Independent of which stem is fitted — unlike the fitted stem's own
     * axes, which are specific to that stem's fit and would make the sign reactive to stem
     * selection.
     */
    femurShaftAxisDirection: NumberArray3 | null;
};

/**
 * Resolve the signed LT-resection distance from the planner's current state: the resection plane
 * comes from the fitted stem (transformed from stem-group into world space), the sign comes from
 * the femur's own shaft-axis study measurement. Returns null until all inputs are available —
 * there is no separate degradation behaviour to design for a missing measurement: if the femur's
 * shaft axis isn't in the study data, the case is broken well beyond this measurement.
 *
 * Pure: takes raw inputs, holds no store/view dependency — the Model store and the resection
 * ViewModel both wire their reactive state into this.
 */
export function resolveLtResection({
    fittedStem,
    stemTransform,
    femurGeometry,
    ltResectionPoint,
    femurShaftAxisDirection,
}: ResolveLtResectionInput): LtResectionResult | null {
    if (
        !ltResectionPoint ||
        !fittedStem ||
        !stemTransform ||
        !femurGeometry ||
        !femurShaftAxisDirection
    ) {
        return null;
    }

    // The operative femur geometry vertices are already in world/CT space.
    const stemGroupTransform = fittedStem.transform.clone().multiply(stemTransform);
    const resectionPlaneOrigin = fittedStem.resectionPlaneLocal.origin
        .clone()
        .applyMatrix4(stemGroupTransform);
    const resectionPlaneNormal = fittedStem.resectionPlaneLocal.normal
        .clone()
        .transformDirection(stemGroupTransform);

    // Already world/CT space (a study measurement, not stem-relative) — no transform needed.
    const siAxis = vector3(femurShaftAxisDirection).negate();

    return calculateLtResectionDistance({
        femurGeometry,
        resectionPlaneOrigin,
        resectionPlaneNormal,
        ltResectionPoint,
        siAxis,
    });
}
